import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Group,
  Loader,
  Alert,
  Table,
  Text,
  Avatar,
  ScrollArea,
  Collapse,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  Select,
  Paper,
  Title,
} from "@mantine/core";
import { LineChart } from "@mantine/charts";
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";

const formatDateTime = iso => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatChartDate = iso => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const TIMEFRAME_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year", label: "This Year" },
  { value: "all_time", label: "All Time" },
];

const getInitials = (first, last) =>
  `${(first ?? "").charAt(0)}${(last ?? "").charAt(0)}`.toUpperCase();

function UsageStatsPage() {
  const { getVerifiedJWT } = useAuth();
  const [users, setUsers] = useState([]);
  const [companyLogins, setCompanyLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [timeframe, setTimeframe] = useState("all_time");

  const fetchData = useCallback(
    async (tf = "all_time") => {
      try {
        setLoading(true);
        setError(null);
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const response = await fetch(
          getApiEndpoint(`/v1/user/company-user-metrics?timeframe=${tf}`),
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          const data = result?.data ?? {};
          setUsers(Array.isArray(data.users) ? data.users : []);
          setCompanyLogins(
            Array.isArray(data.all_company_logins)
              ? data.all_company_logins
              : []
          );
        } else {
          const err = await response.json().catch(() => ({}));
          setError(err?.message || "Failed to load usage stats.");
        }
      } catch (e) {
        setError("An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    },
    [getVerifiedJWT]
  );

  useEffect(() => {
    fetchData(timeframe);
  }, [fetchData, timeframe]);

  const handleTimeframeChange = value => {
    setTimeframe(value);
    setExpandedUserId(null);
  };

  const toggleExpand = userId => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  };

  const chartData = companyLogins.map(entry => ({
    date: formatChartDate(entry.date),
    Logins: entry.login_count,
  }));

  const renderBody = () => {
    if (loading) {
      return (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      );
    }
    if (error) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error}
        </Alert>
      );
    }
    return (
      <Stack gap="lg">
        {chartData.length > 0 && (
          <Paper withBorder p="md">
            <Title order={5} mb="sm">
              Company Logins
            </Title>
            <LineChart
              h={220}
              data={chartData}
              dataKey="date"
              series={[{ name: "Logins", color: "blue.6" }]}
              curveType="monotone"
              withDots={chartData.length <= 60}
              withXAxis
              withYAxis
              xAxisProps={{
                interval: Math.max(0, Math.floor(chartData.length / 10) - 1),
                tick: { fontSize: 11 },
              }}
              yAxisProps={{ tick: { fontSize: 11 } }}
              tooltipAnimationDuration={200}
            />
          </Paper>
        )}
        {users.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No usage data available.
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 32 }} />
                  <Table.Th>User</Table.Th>
                  <Table.Th>Last Login</Table.Th>
                  <Table.Th>Total Logins</Table.Th>
                  <Table.Th>Training Minutes</Table.Th>
                  <Table.Th>Video Minutes</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map(user => {
                  const isExpanded = expandedUserId === user.user_id;
                  const recentLogins = Array.isArray(user.logins)
                    ? user.logins.slice(0, 20)
                    : [];

                  return (
                    <React.Fragment key={user.user_id}>
                      <Table.Tr>
                        <Table.Td>
                          {recentLogins.length > 0 && (
                            <Tooltip
                              label={
                                isExpanded
                                  ? "Hide logins"
                                  : "Show recent logins"
                              }
                            >
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() => toggleExpand(user.user_id)}
                              >
                                {isExpanded ? (
                                  <IconChevronDown size={14} />
                                ) : (
                                  <IconChevronRight size={14} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="sm" wrap="nowrap">
                            <Avatar
                              src={user.profile_image_url || null}
                              radius="xl"
                              size="sm"
                            >
                              {getInitials(user.first_name, user.last_name)}
                            </Avatar>
                            <Box>
                              <Text size="sm" fw={500}>
                                {user.first_name} {user.last_name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {user.email}
                              </Text>
                            </Box>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {formatDateTime(user.last_login_time)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color="blue">
                            {user.total_login_count?.toLocaleString() ?? 0}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {user.total_training_minutes ?? 0} min
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {user.total_video_minutes_watched ?? 0} min
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                      {isExpanded && recentLogins.length > 0 && (
                        <Table.Tr>
                          <Table.Td colSpan={6} style={{ padding: 0 }}>
                            <Collapse in={isExpanded}>
                              <Box px="md" py="sm" bg="gray.0">
                                <Text size="xs" fw={600} mb="xs" c="dimmed">
                                  Recent Logins (last {recentLogins.length})
                                </Text>
                                <Stack gap={4}>
                                  {recentLogins.map((ts, i) => (
                                    <Text key={i} size="xs" c="dimmed">
                                      {formatDateTime(ts)}
                                    </Text>
                                  ))}
                                </Stack>
                              </Box>
                            </Collapse>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="md">
      <Group>
        <Select
          label="Timeframe"
          data={TIMEFRAME_OPTIONS}
          value={timeframe}
          onChange={handleTimeframeChange}
          allowDeselect={false}
          w={180}
        />
      </Group>
      {renderBody()}
    </Stack>
  );
}

export default UsageStatsPage;
